/**
 * Бета (`mode: "beta"`): слоты базы, карточка героя, осколки и звёзды, колесо, сундуки, награды уровня. Чистые функции над
 * `BalanceData.beta` (данные беты) — всё случайное принимает сид сервера, ничего не читает из окружения (как остальной
 * engine). Обычный режим этих функций не вызывает.
 */
import {
  MAX_PLATFORM_COUNT,
  TICK_RATE,
  type BalanceData,
  type BetaDef,
  type BetaReward,
  type ChestType,
  type HeroId,
  type RunHero,
  type WheelSector,
} from "@newfungame/contracts";
import { waveBaseReward } from "../economy/income";
import { MAX_STARS, techModifiers } from "../economy/progression";
import { createRng } from "../sim/rng";
import { heroAttack } from "../sim/stats";
import { levelOf } from "../sim/waves";

export function betaDef(b: BalanceData): BetaDef {
  if (!b.beta) throw new RangeError("balance has no beta section");
  return b.beta;
}

/* ─────────────── слоты базы и копии ─────────────── */

/** Слотов под героев при уровне ветки Tech `base`: `start + ⌊level / everyBaseLevels⌋`, не больше `max` (≤ 16). */
export function slotCount(b: BalanceData, tech: Readonly<Record<string, number>>): number {
  const d = b.beta;
  if (!d) return 6;
  const level = tech[d.baseTechId] ?? 0;
  return Math.min(d.slots.max, MAX_PLATFORM_COUNT, d.slots.start + Math.floor(level / d.slots.everyBaseLevels));
}

/** Результат проверки состава слотов. */
export type LoadoutCheck =
  { ok: true; heroes: RunHero[] } | { ok: false; reason: "too_many_slots" | "copies" | "locked" };

/**
 * Проверить состав забежки: `slots[i]` — герой или `null`. Слотов не больше {@link slotCount}, копий одного героя
 * не больше `copiesMax`, герой должен быть открыт. Уровень и звёзды копий — общие, из прогресса героя.
 */
export function checkLoadout(
  b: BalanceData,
  tech: Readonly<Record<string, number>>,
  slots: readonly (HeroId | null)[],
  progress: readonly { heroId: HeroId; unlocked: boolean; level: number; stars: number }[],
): LoadoutCheck {
  const d = betaDef(b);
  if (slots.length > slotCount(b, tech)) return { ok: false, reason: "too_many_slots" };
  const count = new Map<HeroId, number>();
  const heroes: RunHero[] = [];
  slots.forEach((id, slot) => {
    if (id === null) return;
    const p = progress.find((x) => x.heroId === id);
    if (!p || !p.unlocked) return void count.set(id, -Infinity);
    count.set(id, (count.get(id) ?? 0) + 1);
    heroes.push({ heroId: id, slot, level: p.level, stars: p.stars });
  });
  for (const n of count.values()) {
    if (n === -Infinity || n < 0) return { ok: false, reason: "locked" };
    if (n > d.slots.copiesMax) return { ok: false, reason: "copies" };
  }
  return { ok: true, heroes };
}

/** Слоты по умолчанию: открытые герои по одному, в порядке данных, остальное пусто. */
export function defaultLoadout(
  b: BalanceData,
  tech: Readonly<Record<string, number>>,
  progress: readonly { heroId: HeroId; unlocked: boolean }[],
): (HeroId | null)[] {
  const n = slotCount(b, tech);
  const open = b.heroes.filter((h) => progress.find((p) => p.heroId === h.id)?.unlocked).map((h) => h.id);
  return Array.from({ length: n }, (_, i) => open[i] ?? null);
}

/* ─────────────── карточка героя ─────────────── */

export interface HeroStats {
  /** Урон одного попадания (с уровнем, звёздами и Tech). */
  attack: number;
  /** Выстрелов в минуту. */
  shotsPerMin: number;
  /** Дальность, единиц фиксированной точки (1000 = клетка). */
  range: number;
  /** Шанс крита, промилле. */
  critChance: number;
}

/** Характеристики для карточки героя: те же числа, что использует бой для этого уровня, звёзд и Tech. */
export function heroStats(
  b: BalanceData,
  hero: { heroId: HeroId; level: number; stars: number },
  progress: { tech?: Readonly<Record<string, number>> } = {},
): HeroStats {
  const a = heroAttack(b, hero.heroId, hero.level, hero.stars, techModifiers(b, progress.tech ?? {}));
  if (!a) throw new RangeError(`unknown hero ${hero.heroId}`);
  return {
    attack: a.damage,
    shotsPerMin: Math.round((60 * TICK_RATE) / a.cooldownTicks),
    range: a.range,
    critChance: a.critChance,
  };
}

/** Описание героя карточки (EN/RU) из данных беты. */
export function heroText(b: BalanceData, heroId: HeroId, locale: "en" | "ru" | "uk"): string {
  const t = betaDef(b).heroTexts[heroId];
  if (!t) return "";
  return locale === "ru" || locale === "uk" ? t.ru : t.en;
}

/* ─────────────── осколки и звёзды ─────────────── */

/** Следующая звезда за осколки: нужно осколков и монет (наград волны); `null` — 5★. */
export function shardStarCost(b: BalanceData, stars: number): { shards: number; coinWaves: number } | null {
  if (stars >= MAX_STARS) return null;
  const d = betaDef(b).stars;
  return { shards: d.shards[stars]!, coinWaves: d.coinWaves[stars]! };
}

/* ─────────────── награды ─────────────── */

/** Монеты за `coinWaves` наград волны на лучшей волне игрока (не меньше волны 1). */
export function coinsFor(b: BalanceData, coinWaves: number, bestWave: number): number {
  return Math.max(1, coinWaves * waveBaseReward(b, Math.max(1, bestWave)));
}

/** Что выпало: итоговая награда без случайности (герой для осколков уже выбран). */
export interface Granted {
  coins: number;
  gems: number;
  heroId: HeroId | null;
  shards: number;
}

export interface RewardContext {
  bestWave: number;
  /** Открытые герои (осколки обычных) и премиум-герои (осколки премиум). */
  open: readonly HeroId[];
  premium: readonly HeroId[];
}

function pickWeighted<T extends { weight: number }>(list: readonly T[], r: number): number {
  let n = r % list.reduce((s, x) => s + x.weight, 0);
  for (let i = 0; i < list.length; i++) {
    if (n < list[i]!.weight) return i;
    n -= list[i]!.weight;
  }
  return list.length - 1;
}

/** Разыграть награду: герой осколков выбирается из подходящего списка тем же потоком ГПСЧ. */
export function resolveReward(
  b: BalanceData,
  reward: BetaReward,
  ctx: RewardContext,
  rng: { int(min: number, max: number): number },
): Granted {
  if (reward.kind === "coins")
    return { coins: coinsFor(b, reward.coinWaves, ctx.bestWave), gems: 0, heroId: null, shards: 0 };
  if (reward.kind === "gems") return { coins: 0, gems: reward.gems, heroId: null, shards: 0 };
  const pool = reward.premium ? ctx.premium : ctx.open;
  const heroId = pool.length > 0 ? pool[rng.int(0, pool.length - 1)]! : null;
  return { coins: 0, gems: 0, heroId, shards: heroId ? reward.shards : 0 };
}

/** Колесо: сектор выбирает сервер по своему сиду, веса — из данных. */
export function rollWheel(
  b: BalanceData,
  seed: number,
  ctx: RewardContext,
): { sectorIndex: number; sector: WheelSector; granted: Granted } {
  const sectors = betaDef(b).wheel.sectors;
  const rng = createRng(seed);
  const sectorIndex = pickWeighted(sectors, rng.nextU32());
  const sector = sectors[sectorIndex]!;
  const r = sector.reward;
  const granted: Granted =
    r.kind === "boost" || r.kind === "chest"
      ? { coins: 0, gems: 0, heroId: null, shards: 0 }
      : resolveReward(b, r, ctx, rng);
  return { sectorIndex, sector, granted };
}

/** Сундук: `rolls[type]` бросков по таблице типа; каждый бросок — одна награда. */
export function rollChest(b: BalanceData, type: ChestType, seed: number, ctx: RewardContext): Granted[] {
  const d = betaDef(b).chests;
  const rng = createRng(seed);
  const table = d.table[type];
  const out: Granted[] = [];
  for (let i = 0; i < d.rolls[type]; i++) {
    const entry = table[pickWeighted(table, rng.nextU32())]!;
    out.push(resolveReward(b, entry.reward, ctx, rng));
  }
  return out;
}

/** За прохождение уровня (волна `wave` — последняя подволна): сундук. */
export function levelChest(b: BalanceData, wave: number): ChestType | null {
  const lv = levelOf(b, wave);
  if (!lv || lv.subwave !== lv.subwaves) return null;
  const d = betaDef(b);
  if (lv.isBossLevel) return d.chests.bossLevelChest;
  return lv.level % d.chests.woodEveryLevels === 0 ? "wood" : null;
}

/* ─────────────── состав забежки по слотам (бета) ─────────────── */

type RosterIn = { heroId: HeroId; unlocked: boolean; level: number; stars: number };

/**
 * Состав беты для `refreshRun`: слоты, как стояли (`run.heroes`), уровни и звёзды — из прогресса героя (копии общие),
 * слоты сверх {@link slotCount}, закрытые герои и копии сверх `copiesMax` снимаются; новый открытый герой, которого
 * нет в составе, — в первый свободный слот.
 */
export function betaRoster(
  b: BalanceData,
  placed: readonly RunHero[],
  heroes: readonly RosterIn[],
  tech: Readonly<Record<string, number>>,
): RunHero[] {
  const n = slotCount(b, tech);
  const max = betaDef(b).slots.copiesMax;
  const prog = new Map(heroes.map((h) => [h.heroId, h]));
  const slots: (HeroId | null)[] = Array.from({ length: n }, () => null);
  const copies = new Map<HeroId, number>();
  for (const h of [...placed].sort((x, y) => x.slot - y.slot)) {
    const p = prog.get(h.heroId);
    if (h.slot >= n || !p?.unlocked || (copies.get(h.heroId) ?? 0) >= max || slots[h.slot] !== null) continue;
    slots[h.slot] = h.heroId;
    copies.set(h.heroId, (copies.get(h.heroId) ?? 0) + 1);
  }
  for (const p of heroes) {
    if (!p.unlocked || copies.has(p.heroId)) continue;
    const free = slots.indexOf(null);
    if (free < 0) break;
    slots[free] = p.heroId;
    copies.set(p.heroId, 1);
  }
  return slots.flatMap((id, slot) => {
    const p = id ? prog.get(id) : undefined;
    return id && p ? [{ heroId: id, slot, level: p.level, stars: p.stars }] : [];
  });
}

/**
 * Автосостав: свободные слоты заполняются копиями открытых героев (по кругу, до `copiesMax` на тип), занятые не трогаются.
 * Для кнопки «заполнить» в клиенте и для симулятора.
 */
export function betaFillSlots(
  b: BalanceData,
  placed: readonly RunHero[],
  heroes: readonly RosterIn[],
  tech: Readonly<Record<string, number>>,
): RunHero[] {
  const n = slotCount(b, tech);
  const max = betaDef(b).slots.copiesMax;
  const open = heroes.filter((h) => h.unlocked);
  const out = [...placed];
  const used = new Set(out.map((h) => h.slot));
  const copies = new Map<HeroId, number>();
  for (const h of out) copies.set(h.heroId, (copies.get(h.heroId) ?? 0) + 1);
  for (let slot = 0; slot < n; slot++) {
    if (used.has(slot)) continue;
    // тип с наименьшим числом копий; при равенстве — раньше в данных (открывается раньше)
    const pick = open
      .filter((h) => (copies.get(h.heroId) ?? 0) < max)
      .sort((x, y) => (copies.get(x.heroId) ?? 0) - (copies.get(y.heroId) ?? 0))[0];
    if (!pick) break;
    copies.set(pick.heroId, (copies.get(pick.heroId) ?? 0) + 1);
    out.push({ heroId: pick.heroId, slot, level: pick.level, stars: pick.stars });
  }
  return out.sort((x, y) => x.slot - y.slot);
}

/** Стартовый состав новой забежки беты: открытые герои по очереди, пока не заполнены слоты (копии до `copiesMax`). */
export function betaStartRoster(
  b: BalanceData,
  heroes: readonly RosterIn[],
  tech: Readonly<Record<string, number>>,
): RunHero[] {
  const n = slotCount(b, tech);
  const max = betaDef(b).slots.copiesMax;
  const open = heroes.filter((h) => h.unlocked);
  const out: RunHero[] = [];
  const copies = new Map<HeroId, number>();
  for (let round = 0; out.length < n && open.length > 0; round++) {
    let added = false;
    for (const h of open) {
      if (out.length >= n || (copies.get(h.heroId) ?? 0) >= max) continue;
      copies.set(h.heroId, (copies.get(h.heroId) ?? 0) + 1);
      out.push({ heroId: h.heroId, slot: out.length, level: h.level, stars: h.stars });
      added = true;
      // первый круг — по одному каждого героя; дальше копии «в глубину» для самого сильного (первого)
      if (round > 0) break;
    }
    if (!added) break;
  }
  return out;
}
